import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import ar from "./locales/ar.json";
import en from "./locales/en.json";

export const LANGUAGES = [
    { code: "en", label: "English", dir: "ltr" },
    { code: "ar", label: "العربية", dir: "rtl" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

const STORAGE_KEY = "pm.lang";

/** The server names a language `ar_001`; the bundles are keyed `ar`. */
export function uiLanguage(serverCode: string | null | undefined): LanguageCode | null {
    if (!serverCode) {
        return null;
    }
    const short = serverCode.split(/[-_]/)[0];
    return LANGUAGES.some((item) => item.code === short) ? (short as LanguageCode) : null;
}

export function storedLanguage(): LanguageCode | null {
    try {
        const value = localStorage.getItem(STORAGE_KEY);
        return LANGUAGES.some((item) => item.code === value)
            ? (value as LanguageCode)
            : null;
    } catch {
        return null;
    }
}

export function rememberLanguage(code: LanguageCode) {
    try {
        localStorage.setItem(STORAGE_KEY, code);
    } catch {
        /* a private window may refuse storage; the choice simply is not kept */
    }
}

export function applyDirection(code: string) {
    const entry = LANGUAGES.find((item) => item.code === code) ?? LANGUAGES[0];
    document.documentElement.lang = entry.code;
    document.documentElement.dir = entry.dir;
    document.body.classList.toggle("o_rtl", entry.dir === "rtl");
}

/** Follow the language the account carries, which is what the API answers in. */
export async function followServerLanguage(serverCode: string | null | undefined) {
    const code = uiLanguage(serverCode);
    if (code && code !== i18n.language) {
        await i18n.changeLanguage(code);
    }
}

void i18n.use(initReactI18next).init({
    resources: { en: { translation: en }, ar: { translation: ar } },
    lng: storedLanguage() ?? "en",
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    returnNull: false,
});

applyDirection(i18n.language);

i18n.on("languageChanged", (code) => {
    applyDirection(code);
    rememberLanguage(code as LanguageCode);
});

export default i18n;
